import { hasMouse, onMouse } from '../utils/has-mouse';

const shop = document.querySelector('.m-shop') as HTMLElement;
const products = Array.from(shop.querySelectorAll('.shop-item')) as HTMLElement[];
const currentImage = shop.querySelector('.m-shop__list-image') as HTMLElement;
const toggleButtons = Array.from(shop.querySelectorAll('.m-shop__view-item button'));

// Media strip on grid cards (templates/collection.liquid), mouse devices only:
// hovering shows media data-start (2nd, or 1st when "Disable hover image" is ticked),
// the arrows browse the rest, all in the order they are in Shopify. A photo only gets
// its src, and a video only starts loading, once one of those happens, so phones
// never download them.
type Cell = HTMLImageElement | HTMLVideoElement;

const isVideo = (cell?: Cell): cell is HTMLVideoElement => cell?.tagName === 'VIDEO';

const loadPhoto = (cell?: Cell, eager = false) => {
  if (!cell) return;

  if (isVideo(cell)) {
    if (cell.preload === 'none') {
      cell.preload = 'auto';
      cell.load();
    }
    return;
  }

  if (eager) cell.loading = 'eager';
  if (cell.dataset.srcset && !cell.srcset) cell.srcset = cell.dataset.srcset;
  if (cell.dataset.src && !cell.getAttribute('src')) cell.src = cell.dataset.src;
};

// only the media on show plays
const playCell = (gallery: HTMLElement, cell?: Cell) => {
  galleryPhotos(gallery).forEach((other) => {
    if (!isVideo(other)) return;
    if (other === cell) other.play().catch(() => {});
    else other.pause();
  });
};

const galleries = Array.from(shop.querySelectorAll('.m-shop__items .shop-item__gallery')) as HTMLElement[];

const galleryPhotos = (gallery: HTMLElement) =>
  Array.from(
    gallery.querySelectorAll('.shop-item__gallery-image, .shop-item__gallery-video')
  ) as Cell[];

const wrapIndex = (index: number, count: number) => ((index % count) + count) % count;

// load a photo and its neighbours, so the next click is instant
const loadAround = (gallery: HTMLElement, index: number) => {
  const photos = galleryPhotos(gallery);
  [index - 1, index, index + 1].forEach((i) => loadPhoto(photos[wrapIndex(i, photos.length)], true));
};

const showPhoto = (gallery: HTMLElement, index: number, load = true) => {
  const track = gallery.querySelector('.shop-item__gallery-track') as HTMLElement;
  const photos = galleryPhotos(gallery);
  const current = wrapIndex(index, photos.length);
  const photo = photos[current];

  gallery.dataset.index = String(current);

  // instant switch, like phoebephilo.com (only if no newer click happened meanwhile)
  const move = () => {
    if (gallery.dataset.index === String(current)) track.style.transform = `translateX(${-100 * current}%)`;
  };

  if (!load) {
    move();
    return;
  }

  loadAround(gallery, current);
  playCell(gallery, photo);

  // keep the current photo on screen until the new one has arrived,
  // otherwise the empty slot shows the 1st photo underneath for a moment
  if (isVideo(photo) || (photo.complete && photo.naturalWidth > 0)) {
    move();
  } else {
    photo.addEventListener('load', move, { once: true });
    photo.addEventListener('error', move, { once: true });
  }
};

if (galleries.length) {
  // Everything below only happens where a mouse is in use (see utils/has-mouse.ts),
  // so a phone neither shows the strip nor downloads any of its photos.
  onMouse(() => {
    galleries.forEach((gallery) => {
      // position each strip on its hover photo and load that one photo
      showPhoto(gallery, Number(gallery.dataset.start), false);
      loadPhoto(galleryPhotos(gallery)[Number(gallery.dataset.start)]);
    });

    products.forEach((product) => {
      const gallery = product.querySelector('.shop-item__gallery') as HTMLElement | null;
      if (!gallery) return;

      // on hover, get the photos either side of the hover photo ready for the arrows
      product.addEventListener('mouseenter', () => loadAround(gallery, Number(gallery.dataset.start)));

      product.querySelectorAll('.shop-item__arrow').forEach((arrow) => {
        arrow.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          const direction = Number((arrow as HTMLElement).dataset.direction);
          showPhoto(gallery, Number(gallery.dataset.index) + direction);
        });
      });

      // back to the hover photo once the card has faded out
      // (fade waits 0.15s, then takes 0.3s: see modules/shop/_grid.scss)
      product.addEventListener('mouseleave', () => {
        playCell(gallery);
        window.setTimeout(() => {
          if (!product.matches(':hover')) showPhoto(gallery, Number(gallery.dataset.start), false);
        }, 500);
      });
    });
  });
}

// Phones, one-photo view: each card's photos become a swipe carousel, the same one
// (Flickity) as the product page slider, without arrows (modules/shop/_grid.scss).
// A card's first two photos load when it comes near the screen, then the photos
// either side of the one in view. The other views don't get a carousel.
const phoneWidth = window.matchMedia('(max-width: 44.9375rem)');
const carousels = new Map<HTMLElement, any>();

const loadNear = (gallery: HTMLElement, index: number) => {
  const photos = galleryPhotos(gallery);
  [index - 1, index, index + 1].forEach((i) => loadPhoto(photos[i], true));
};

const nearScreen = 'IntersectionObserver' in window
  ? new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const gallery = entry.target as HTMLElement;
          const index = carousels.get(gallery)?.selectedIndex ?? 0;

          if (!entry.isIntersecting) {
            playCell(gallery);
            return;
          }

          loadNear(gallery, index);
          playCell(gallery, galleryPhotos(gallery)[index]);
        });
      },
      { rootMargin: '300px 0px' }
    )
  : null;

const wantCarousels = () =>
  navigator.maxTouchPoints > 0 &&
  phoneWidth.matches &&
  !hasMouse() &&
  shop.classList.contains('m-shop--grid') &&
  typeof (window as any).Flickity === 'function';

const updateCarousels = () => {
  if (wantCarousels()) {
    // show the strips first: a carousel measures itself as it starts, and a hidden
    // one would come out zero-wide and refuse to be dragged (iOS Safari)
    shop.classList.add('m-shop--swipe');
    shop.getBoundingClientRect();
    // the strip covers the card, so the card's own video keeps quiet
    shop.querySelectorAll('.shop-item__video').forEach((video) => (video as HTMLVideoElement).pause());

    galleries.forEach((gallery) => {
      if (carousels.has(gallery)) return;

      const flkty = new (window as any).Flickity(gallery, {
        cellSelector: '.shop-item__gallery-image, .shop-item__gallery-video',
        cellAlign: 'left',
        contain: true,
        prevNextButtons: false,
        pageDots: false,
        setGallerySize: false,
        accessibility: false,
      });
      flkty.on('change', (index: number) => {
        loadNear(gallery, index);
        playCell(gallery, galleryPhotos(gallery)[index]);
      });
      // also as soon as a finger touches the card, in case it hadn't loaded yet
      flkty.on('pointerDown', () => loadNear(gallery, flkty.selectedIndex));
      carousels.set(gallery, flkty);

      // and measure again once the photos are in place, in case anything shifted
      requestAnimationFrame(() => flkty.resize());

      if (nearScreen) nearScreen.observe(gallery);
      else loadNear(gallery, 0);
    });
  } else {
    carousels.forEach((flkty, gallery) => {
      flkty.destroy();
      playCell(gallery);
      nearScreen?.unobserve(gallery);
    });
    carousels.clear();
    shop.classList.remove('m-shop--swipe');
  }
};

if (galleries.length) phoneWidth.addEventListener('change', updateCarousels);

// A card whose first media is a video (templates/collection.liquid): it downloads and
// plays only while it is on screen, and it is muted, so browsers allow it to start.
const cardVideos = Array.from(shop.querySelectorAll('.shop-item__video')) as HTMLVideoElement[];

if (cardVideos.length && 'IntersectionObserver' in window) {
  const playing = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target as HTMLVideoElement;

        if (!entry.isIntersecting || carousels.size) {
          video.pause();
          return;
        }

        if (video.preload === 'none') {
          video.preload = 'auto';
          video.load();
        }
        video.play().catch(() => {});
      });
    },
    { rootMargin: '200px 0px' }
  );

  cardVideos.forEach((video) => playing.observe(video));
}

// update current product for list view: the hovered card's picture, or for a card that opens
// on a video, its first photo (data-list-photo, snippets/shop-card.liquid), never the video
const updateCurrentProduct = (product: HTMLElement) => {
  const image = product.querySelector('.shop-item__image') as HTMLElement;
  const listPhoto = image.dataset.listPhoto;
  if (listPhoto) {
    const picture = document.createElement('picture');
    const photo = document.createElement('img');
    photo.src = listPhoto;
    photo.alt = image.querySelector('video')?.getAttribute('aria-label') || '';
    photo.className = 'img-loader__high';
    if (product.classList.contains('is-sold-out')) photo.classList.add('is-sold-out');
    picture.append(photo);
    currentImage.replaceChildren(picture);
  } else {
    currentImage.innerHTML = image.innerHTML;
  }

  products.forEach((product) => {
    product.classList.remove('is-active');
  });

  product.classList.add('is-active');
}

// highglight first product on list view
updateCurrentProduct(products[0]);

products.forEach((product) => {
  product.addEventListener("mouseenter", (e) => {
    updateCurrentProduct(e.currentTarget as HTMLElement);
  });
});

// check view from session storage
const sessionView = sessionStorage.getItem('shop-view');
const view = sessionView ? sessionView : 'grid';

shop.classList.add(`m-shop--${view}`);
toggleButtons.forEach((button) => {
  if (button.parentElement?.dataset.view === view) {
    button.classList.add('is-active');
  } else {
    button.classList.remove('is-active');
  }
});

// carousels once the view is known (Flickity loads with defer, before this module runs)
if (typeof (window as any).Flickity === 'function') updateCarousels();
else window.addEventListener('load', updateCarousels);

// toggle view
toggleButtons.forEach((button) => {
  button.addEventListener('click', (e) => {
    const button = e.target as HTMLButtonElement;
    const li = button.parentElement as HTMLLIElement;
    const view = li.dataset.view;

    // update active button
    toggleButtons.forEach((btn) => {
      btn.classList.remove('is-active');
    });
    button.classList.add('is-active');

    // update shop view
    shop.classList.remove('m-shop--grid', 'm-shop--list', 'm-shop--grid-2');
    shop.classList.add(`m-shop--${view}`);
    sessionStorage.setItem("shop-view", `${view}`);

    if (view === 'list') {
      updateCurrentProduct(products[0]);
    }

    updateCarousels();
  });
});